import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useAuth } from '../AuthContext'
import ApiEndpoints from './ApiEndpoints'
import {
  getValidInstituteIdFromCookie,
  hasValidInstituteIdCookie,
} from '../../utils/instituteIdCookie'
import {
  clearCachedNeedPlan,
  readCachedNeedPlan,
  writeCachedNeedPlan,
} from '../../utils/subscriptionNeedPlanStorage'

const SubscriptionNeedPlanContext = createContext({
  needPlan: false,
  loading: false,
  checked: false,
  refreshNeedPlanCheck: () => {},
})

let inFlightNeedPlanRequest = null
let inFlightNeedPlanKey = null

const fetchNeedPlanOnce = async (requestKey) => {
  if (inFlightNeedPlanRequest && inFlightNeedPlanKey === requestKey) {
    return inFlightNeedPlanRequest
  }

  inFlightNeedPlanKey = requestKey
  inFlightNeedPlanRequest = ApiEndpoints.getSubscriptionNeedPlan().finally(() => {
    if (inFlightNeedPlanKey === requestKey) {
      inFlightNeedPlanRequest = null
      inFlightNeedPlanKey = null
    }
  })

  return inFlightNeedPlanRequest
}

export const isParentUserType = (user) => {
  const userType = String(user?.user_type || '').toLowerCase()
  return userType === 'parents' || userType === 'parent'
}

const buildCheckKey = (instituteId, userId) =>
  `${instituteId ?? 'none'}:${userId ?? 'unknown'}`

export const SubscriptionNeedPlanProvider = ({ children }) => {
  const { user } = useAuth()
  const [needPlan, setNeedPlan] = useState(false)
  const [loading, setLoading] = useState(false)
  const [checked, setChecked] = useState(false)
  const lastCompletedCheckKeyRef = useRef('')

  const instituteId = getValidInstituteIdFromCookie()
  const userId = user?.id ?? 'unknown'
  const shouldCheck =
    Boolean(user) && !isParentUserType(user) && hasValidInstituteIdCookie()

  const runCheck = useCallback(
    async ({ force = false } = {}) => {
      if (!shouldCheck || instituteId == null) {
        setNeedPlan(false)
        setChecked(true)
        setLoading(false)
        return
      }

      const checkKey = buildCheckKey(instituteId, userId)

      if (!force && lastCompletedCheckKeyRef.current === checkKey) {
        return
      }

      if (!force) {
        const cached = readCachedNeedPlan(instituteId, userId)
        if (cached !== null) {
          setNeedPlan(cached)
          setChecked(true)
          setLoading(false)
          lastCompletedCheckKeyRef.current = checkKey
          return
        }
      }

      setLoading(true)
      try {
        const response = await fetchNeedPlanOnce(checkKey)
        const value = Boolean(response.data?.data?.need_plan)
        writeCachedNeedPlan(instituteId, userId, value)
        setNeedPlan(value)
        lastCompletedCheckKeyRef.current = checkKey
      } catch {
        setNeedPlan(false)
      } finally {
        setLoading(false)
        setChecked(true)
      }
    },
    [shouldCheck, instituteId, userId],
  )

  useEffect(() => {
    if (!user) {
      setNeedPlan(false)
      setChecked(false)
      setLoading(false)
      lastCompletedCheckKeyRef.current = ''
      return
    }

    runCheck()
  }, [user, runCheck])

  const refreshNeedPlanCheck = useCallback(() => {
    if (!shouldCheck || instituteId == null) return Promise.resolve()
    clearCachedNeedPlan()
    lastCompletedCheckKeyRef.current = ''
    return runCheck({ force: true })
  }, [runCheck, shouldCheck, instituteId])

  return (
    <SubscriptionNeedPlanContext.Provider
      value={{ needPlan, loading, checked, refreshNeedPlanCheck }}
    >
      {children}
    </SubscriptionNeedPlanContext.Provider>
  )
}

export const useSubscriptionNeedPlan = () => useContext(SubscriptionNeedPlanContext)
